import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { Box, ButtonBase, Divider, Link, Paper, Popper, Stack, Typography } from '@mui/material';
import { ACCESSIBLE_ACCOUNTS_QUERY_KEY, ACCOUNT_RELATIONSHIP_LABEL } from '@/consts/accounts';
import {
  BUSINESS_SWITCHER_ANCHOR,
  BUSINESS_UI_STATUS,
  BUSINESS_UI_STATUS_LABEL,
  businessUiStatusColor,
} from '@/consts/businesses';
import { BUSINESS_HEADER_REFUSED_MESSAGE } from '@/consts/businessForbidden';
import { useBusiness } from '@/contexts/BusinessContext';
import {
  IMPERSONATION_REVOKED_MESSAGE,
  IMPERSONATION_REVOKED_TITLE,
} from '@/auth/impersonationSession';
import { CustomAvatar } from '@/components/custom-avatar';
import Iconify from '@/components/iconify';
import Label from '@/components/label';
import MenuPopover from '@/components/menu-popover';
import { useNotify } from '@/components/snackbar';
import { PATH_DASHBOARD } from '@/routes/paths';
import { getAccessibleAccounts } from '@/services/accounts';
import { switchAccountAndBusiness } from './switchAccountAndBusiness';

const getInitials = (name) =>
  String(name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const AccountAvatar = ({ name, size = 36 }) => (
  <CustomAvatar
    color="default"
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      typography: 'caption',
      fontWeight: 'fontWeightBold',
    }}
  >
    {getInitials(name)}
  </CustomAvatar>
);

AccountAvatar.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number,
};

const hasStatusToShow = (business) => Boolean(business?.uiStatus) && Boolean(business?.hasDocument);

const isApprovedWithDocument = (business) =>
  hasStatusToShow(business) && business.uiStatus === BUSINESS_UI_STATUS.APPROVED;

const StatusLabel = ({ business }) => (
  <Label
    variant="soft"
    color={businessUiStatusColor(business)}
    sx={{ flexShrink: 0, height: 20, px: 0.75, fontSize: 11, textTransform: 'none' }}
  >
    {BUSINESS_UI_STATUS_LABEL[business.uiStatus]}
  </Label>
);

StatusLabel.propTypes = {
  business: PropTypes.object,
};

const SectionTitle = ({ children }) => (
  <Typography variant="caption" sx={{ px: 1, py: 0.5, display: 'block', color: 'text.secondary' }}>
    {children}
  </Typography>
);

SectionTitle.propTypes = {
  children: PropTypes.node,
};

const rowSx = {
  p: 1,
  gap: 1.25,
  width: 1,
  borderRadius: 1.25,
  justifyContent: 'flex-start',
  textAlign: 'left',
};

const AccountRow = ({ account, isOpen, onClick, opensPanel }) => {
  const hasPanel = opensPanel ?? account.businesses.length > 1;

  return (
    <ButtonBase
      disabled={!onClick}
      aria-expanded={hasPanel ? isOpen : undefined}
      onClick={onClick}
      sx={{
        ...rowSx,
        ...(isOpen && { bgcolor: 'action.selected' }),
        ...(onClick && { '&:hover': { bgcolor: 'action.hover' } }),
      }}
    >
      <AccountAvatar name={account.name} />

      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" noWrap>
          {account.name}
        </Typography>
        <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
          {account.documentLabel}
        </Typography>
        <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
          {ACCOUNT_RELATIONSHIP_LABEL[account.relationship]}
        </Typography>
      </Box>

      {hasPanel && (
        <Iconify
          icon="eva:chevron-right-fill"
          width={18}
          sx={{ flexShrink: 0, color: 'text.secondary' }}
        />
      )}
    </ButtonBase>
  );
};

AccountRow.propTypes = {
  account: PropTypes.object,
  isOpen: PropTypes.bool,
  onClick: PropTypes.func,
  opensPanel: PropTypes.bool,
};

const BusinessesPanel = ({ account, anchorEl, activeBusinessId, onSelect, onCreate }) => (
  <Popper
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    placement="right-start"
    disablePortal
    modifiers={[{ name: 'offset', options: { offset: [-8, 24] } }]}
    sx={{ zIndex: 1 }}
  >
    <Paper
      sx={{
        p: 1,
        width: 280,
        boxShadow: (theme) => theme.customShadows?.dropdown,
        borderRadius: 1.5,
      }}
    >
      <SectionTitle>{`Negócios de ${account.name}`}</SectionTitle>

      <Stack sx={{ mt: 0.5 }}>
        {account.businesses.map((business) => {
          const isActive = String(business.id) === String(activeBusinessId);

          return (
            <ButtonBase
              key={business.id}
              aria-current={isActive}
              onClick={() => onSelect(account, business.id)}
              sx={{
                ...rowSx,
                ...(isActive && { bgcolor: 'action.selected' }),
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="subtitle2" noWrap>
                  {business.name}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ display: 'block', color: 'text.secondary' }}
                >
                  {business.documentLabel}
                </Typography>
              </Box>

              {hasStatusToShow(business) && <StatusLabel business={business} />}
            </ButtonBase>
          );
        })}
      </Stack>

      {/* Novo Negócio só na própria conta: criar negócio em conta compartilhada está fora do escopo. */}
      {account.isOwn && (
        <>
          <Divider sx={{ my: 1 }} />

          <ButtonBase
            onClick={onCreate}
            sx={{ ...rowSx, color: 'primary.main', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Iconify icon="eva:plus-fill" width={18} />
            <Typography variant="subtitle2">Novo Negócio</Typography>
          </ButtonBase>
        </>
      )}
    </Paper>
  </Popper>
);

BusinessesPanel.propTypes = {
  account: PropTypes.object,
  anchorEl: PropTypes.object,
  activeBusinessId: PropTypes.string,
  onSelect: PropTypes.func,
  onCreate: PropTypes.func,
};

export default function AccountSwitcher({ accounts, currentAccount }) {
  const { activeBusiness } = useBusiness();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notify = useNotify();

  const [openPopover, setOpenPopover] = useState(null);
  const [panel, setPanel] = useState(null);
  // O painel de negócios abre ao lado da linha clicada, e não no topo do menu: aberto longe da
  // conta que o abriu, não ficava claro de quem eram os negócios.
  const [panelAnchor, setPanelAnchor] = useState(null);

  const otherAccounts = accounts.filter((account) => account.ownerId !== currentAccount.ownerId);

  const visibleActiveBusiness =
    currentAccount.businesses.find(
      (business) => String(business.id) === String(activeBusiness?.id)
    ) ??
    currentAccount.businesses.find((business) => business.isDefault) ??
    currentAccount.businesses[0] ??
    null;

  const hasManyBusinesses = currentAccount.businesses.length > 1;
  // Na própria conta a lista sempre abre, mesmo com um negócio só: é por ela que se chega ao
  // Novo Negócio (decisão do Rafael na daily de 03/11, CBK-682; o RF-03 do 4.2 deixa de valer
  // para a própria conta). Em conta compartilhada segue o RF-03.
  const currentAccountOpensPanel = hasManyBusinesses || currentAccount.isOwn;

  const documentLabel = visibleActiveBusiness?.documentLabel || currentAccount.documentLabel;

  const handleClose = () => {
    setPanel(null);
    setPanelAnchor(null);
    setOpenPopover(null);
  };

  const togglePanel = (account, anchor) => {
    const isClosing = panel?.ownerId === account.ownerId;
    setPanel(isClosing ? null : account);
    setPanelAnchor(isClosing ? null : anchor);
  };

  // A lista pode ter mudado desde que o menu abriu (convite cancelado, negócio
  // reprovado): confere de novo antes de trocar, como o menu de perfil fazia.
  const handleSwitch = async (account, businessId) => {
    if (
      account.ownerId === currentAccount.ownerId &&
      String(businessId) === String(visibleActiveBusiness?.id)
    ) {
      handleClose();
      return;
    }

    try {
      const freshAccounts = await queryClient.fetchQuery({
        queryKey: ACCESSIBLE_ACCOUNTS_QUERY_KEY,
        queryFn: getAccessibleAccounts,
      });
      const freshAccount = (freshAccounts ?? []).find(
        (candidate) => candidate.ownerId === account.ownerId
      );
      const isStillVisible = freshAccount?.businesses.some(
        (business) => String(business.id) === String(businessId)
      );

      if (!freshAccount) {
        handleClose();
        notify.warning(IMPERSONATION_REVOKED_TITLE, {
          autoHideDuration: 8000,
          subtitle: IMPERSONATION_REVOKED_MESSAGE,
        });
        return;
      }

      if (!isStillVisible) {
        handleClose();
        notify.warning(BUSINESS_HEADER_REFUSED_MESSAGE);
        return;
      }

      switchAccountAndBusiness({ account: freshAccount, businessId });
    } catch (error) {
      notify.error('Não foi possível trocar de conta agora. Tente novamente.', {
        autoHideDuration: 5000,
      });
    }
  };

  const handleAccountClick = (account, anchor) => {
    if (account.businesses.length > 1) {
      togglePanel(account, anchor);
      return;
    }

    // RF-09 do 4.2: conta com um negócio troca direto.
    handleSwitch(account, account.businesses[0]?.id);
  };

  const handleCreateBusiness = () => {
    handleClose();
    navigate(PATH_DASHBOARD.business.new);
  };

  const handleOpenAccountDetails = () => {
    handleClose();
    navigate(PATH_DASHBOARD.business.account);
  };

  return (
    <>
      <ButtonBase
        data-tour={BUSINESS_SWITCHER_ANCHOR}
        aria-haspopup="menu"
        aria-expanded={Boolean(openPopover)}
        aria-label={`Conta em uso: ${currentAccount.name}. Trocar de conta ou de negócio`}
        onClick={(event) => setOpenPopover(event.currentTarget)}
        sx={{
          p: 1.25,
          width: 1,
          gap: 1.25,
          borderRadius: 1.5,
          justifyContent: 'flex-start',
          border: (theme) => `solid 1px ${theme.palette.divider}`,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <AccountAvatar name={currentAccount.name} />

        <Box sx={{ minWidth: 0, flexGrow: 1, textAlign: 'left' }}>
          <Typography variant="subtitle2" noWrap>
            {currentAccount.name}
          </Typography>

          {hasManyBusinesses && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                {visibleActiveBusiness?.name}
              </Typography>
              {hasStatusToShow(visibleActiveBusiness) && (
                <StatusLabel business={visibleActiveBusiness} />
              )}
            </Stack>
          )}

          <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
            {documentLabel}
          </Typography>

          {/* Com um negócio só, o selo Aprovado ganha linha própria (4.1.2 RF-01, 5.12; CBK-679):
              ao lado do nome ou do documento ele cortava o texto na largura do menu lateral
              (decidido com o Murilo em 04/11). */}
          {!hasManyBusinesses && isApprovedWithDocument(visibleActiveBusiness) && (
            <Box sx={{ mt: 0.25 }}>
              <StatusLabel business={visibleActiveBusiness} />
            </Box>
          )}
        </Box>

        <Iconify
          icon="lucide:chevrons-up-down"
          width={18}
          sx={{ flexShrink: 0, color: 'text.secondary' }}
        />
      </ButtonBase>

      <MenuPopover
        open={openPopover}
        onClose={handleClose}
        disabledArrow
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ p: 1, mt: 1, width: openPopover?.offsetWidth }}
      >
        <Box>
          <SectionTitle>Conta em uso</SectionTitle>

          <AccountRow
            account={currentAccount}
            isOpen={panel?.ownerId === currentAccount.ownerId}
            opensPanel={currentAccountOpensPanel}
            onClick={
              currentAccountOpensPanel
                ? (event) => togglePanel(currentAccount, event.currentTarget)
                : undefined
            }
          />

          <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={handleOpenAccountDetails}
            sx={{ px: 1, py: 0.5, ml: 6, display: 'inline-flex', alignItems: 'center' }}
          >
            Ver dados da conta
            <Iconify icon="eva:chevron-right-fill" width={16} />
          </Link>

          {otherAccounts.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />

              <SectionTitle>Trocar de conta</SectionTitle>

              <Stack sx={{ mt: 0.5 }}>
                {otherAccounts.map((account) => (
                  <AccountRow
                    key={account.ownerId}
                    account={account}
                    isOpen={panel?.ownerId === account.ownerId}
                    onClick={(event) => handleAccountClick(account, event.currentTarget)}
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>

        {panel && (
          <BusinessesPanel
            account={panel}
            anchorEl={panelAnchor}
            activeBusinessId={
              panel.ownerId === currentAccount.ownerId ? String(visibleActiveBusiness?.id) : null
            }
            onSelect={handleSwitch}
            onCreate={handleCreateBusiness}
          />
        )}
      </MenuPopover>
    </>
  );
}

AccountSwitcher.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentAccount: PropTypes.object.isRequired,
};
